import React, { useState, useEffect } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { updateDashboardSettings } from '../../actions/user/action';
import { getOrganizationTree } from '../../actions/organization/index';
import { useDispatch } from 'react-redux';

const DashboardSwitcher = () => {
  const { user } = useSelector((state) => state?.user || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);

  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [userOrganization, setUserOrganization] = useState(null);

  useEffect(() => {
    getUserOrganization();
  }, []);

  const getUserOrganization = async () => {
    try {
      const res = await getOrganizationTree(
        {
          input: {
            organizationId: user?.organizationId || 'DANUBE_GROUP',
            isMenu: true,
          },
        },
        dispatch
      );

      if (res?.organizationId) {
        setUserOrganization(res);
      } else {
        setUserOrganization(null);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const toggle = () => setIsOpen(!isOpen);

  const handleBrandClick = (brandId) => {
    setSelectedBrandId(brandId === selectedBrandId ? null : brandId);
    setSelectedCountryCode(null);
    setSelectedStoreId(null);
    updateDashboardSettingsFunc({
      organizationId: userOrganization?.organizationId,
      brandId: brandId === selectedBrandId ? null : brandId,
      countryCode: null,
      storeId: null,
    });
  };

  const handleCountryClick = (countryCode) => {
    setSelectedCountryCode(
      countryCode === selectedCountryCode ? null : countryCode
    );
    setSelectedStoreId(null);
    updateDashboardSettingsFunc({
      organizationId: userOrganization?.organizationId,
      brandId: selectedBrandId,
      countryCode: countryCode === selectedCountryCode ? null : countryCode,
      storeId: null,
    });
  };

  const handleStoreClick = (storeId) => {
    setSelectedStoreId(storeId === selectedStoreId ? null : storeId);
    updateDashboardSettingsFunc({
      organizationId: userOrganization?.organizationId,
      brandId: selectedBrandId,
      countryCode: selectedCountryCode,
      storeId: storeId === selectedStoreId ? null : storeId,
    });
  };

  useEffect(() => {
    if (user?.dashboardSettings) {
      setSelectedBrandId(user?.dashboardSettings?.brandId);
      setSelectedCountryCode(user?.dashboardSettings?.countryCode);
      setSelectedStoreId(user?.dashboardSettings?.storeId);
    }
  }, [user]);

  const updateDashboardSettingsFunc = async (data) => {
    try {
      const inputData = {
        input: data,
      };
      await updateDashboardSettings(inputData);
    } catch (e) {
      console.log(e);
    }
  };

  const selectedBrand = userOrganization?.brands?.find(
    (brand) => brand?.brandId === selectedBrandId
  );
  const selectedCountry = selectedBrand?.countryList?.find(
    (country) => country?.countryCode === selectedCountryCode
  );
  const selectedStore = selectedCountry?.stores?.find(
    (store) => store?.storeId === selectedStoreId
  );

  const brandName = selectedBrand?.name;
  const countryName = selectedCountry?.flag
    ? `${selectedCountry?.flag} ${selectedCountry?.name}`
    : null;
  const storeName = selectedStore?.name;
  const organizationName = userOrganization?.name;

  return (
    <>
      <span
        className="border border-success bg-success-subtle py-1 pe-2 ps-3 d-flex align-items-center gap-2 fs-14 rounded-3 text-success breadcrumbs position-relative cursor-pointer"
        onClick={toggle}
      >
        <Link className="d-flex align-items-center gap-1 text-success" to="#">
          {organizationName}
        </Link>
        {(countryName || storeName || brandName) && (
          <i className="bx bxs-chevron-right"></i>
        )}

        {brandName && (
          <span className="d-flex align-items-center gap-1">
            <span>{brandName}</span>
            {(countryName || storeName) && (
              <i className="bx bxs-chevron-right"></i>
            )}
          </span>
        )}
        {countryName && (
          <span className="d-flex align-items-center gap-1">
            <span>{countryName}</span>
            {storeName && <i className="bx bxs-chevron-right"></i>}
          </span>
        )}
        {storeName && (
          <span className="d-flex align-items-center gap-1">
            <span>{storeName}</span>
          </span>
        )}
      </span>

      <Offcanvas
        isOpen={isOpen}
        toggle={toggle}
        direction="end"
        style={{ width: '24vw' }}
        onClosed={() => {
          navigate('/dashboard');
          window.location.reload();
        }}
      >
        <OffcanvasHeader toggle={toggle} className="border-bottom">
          {userOrganization?.name}
        </OffcanvasHeader>

        <OffcanvasBody className="p-3">
          <div className="nested-menu d-flex flex-column gap-3">
            {userOrganization?.brands?.map((brand) => (
              <div
                key={brand?.brandId}
                className="rounded border shadow-none p-2 position-relative"
              >
                <div
                  className={classNames(
                    'd-flex align-items-center justify-content-between px-3 py-2 rounded fw-semibold cursor-pointer transition',
                    {
                      'bg-light': selectedBrandId === brand?.brandId,
                    }
                  )}
                  onClick={() => {
                    handleBrandClick(brand?.brandId);
                  }}
                >
                  <span>{brand?.name}</span>
                  <i
                    className={classNames(
                      'bx',
                      selectedBrandId === brand?.brandId
                        ? 'bx-chevron-down'
                        : 'bx-chevron-right'
                    )}
                  ></i>
                </div>

                {selectedBrandId === brand?.brandId && (
                  <div className="ps-3 pt-2 border-start border-1 ms-2">
                    {brand?.countryList?.map((country) => (
                      <div
                        key={country?.countryCode}
                        className="mb-2 position-relative"
                      >
                        <div
                          className={classNames(
                            'd-flex align-items-center justify-content-between px-3 py-2 rounded cursor-pointer fw-semibold transition',
                            {
                              'bg-light':
                                selectedCountryCode === country?.countryCode,
                            }
                          )}
                          onClick={() =>
                            handleCountryClick(country?.countryCode)
                          }
                        >
                          <span>{`${country?.flag} ${country?.name}`}</span>
                          {country?.stores?.length > 0 ? (
                            <i
                              className={classNames(
                                'bx',
                                selectedCountryCode === country?.countryCode
                                  ? 'bx-chevron-down'
                                  : 'bx-chevron-right'
                              )}
                            ></i>
                          ) : null}
                        </div>

                        {selectedCountryCode === country?.countryCode &&
                          country?.stores?.length > 0 && (
                            <div className="ps-4 pt-2 border-start border-1 ms-2">
                              {country?.stores?.map((store) => (
                                <button
                                  key={store?.storeId}
                                  className={classNames(
                                    'btn btn-sm text-start rounded px-3 py-2 fw-semibold transition shadow-none',
                                    {
                                      'bg-light':
                                        selectedStoreId === store?.storeId,
                                    }
                                  )}
                                  onClick={() =>
                                    handleStoreClick(store?.storeId)
                                  }
                                >
                                  {store?.name}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default DashboardSwitcher;
